<script lang="ts">
	import "bulma-slider/dist/css/bulma-slider.min.css";
	import "bulma-switch/dist/css/bulma-switch.min.css";

	import { fly } from "svelte/transition";

	import { onMount, onDestroy } from "svelte";
	import Fa from "svelte-fa";
	import { faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons";

	import { dldl, goto, yyyy, zzzz } from "./store.svelte";
	import { addOnsData } from "./lib/addOnsData";
	import { BAND1_MONTHLY, BAND2_MONTHLY, BAND1_ANNUAL, BAND2_ANNUAL, ANNUAL_MIN_USERS, ENTERPRISE_MIN_USERS, MIN_USERS } from "./lib/pricingConstants";

	let isYearly: boolean = $state(false);
	let ready: boolean = $state(false);
	let numUsers: number = $state(15);
	let minUsersEl: HTMLElement | undefined = $state();
	let activePopover: string | null = $state(null);
	let showOnboarding: boolean = $state(false);
	let onboardingPlan: 'jumpstart' | 'enterprise' = $state('jumpstart');

	// Paid add-ons configuration with reactive enabled state - filter out items that should be omitted from pricing
	let addOns = $state(addOnsData.filter(addon => !addon.omitFromPricing && addon.readyForAction !== false).map(addon => ({ ...addon, enabled: false })));

	let isEnterprise: boolean = $derived(numUsers > ENTERPRISE_MIN_USERS);

	$effect(() => {
		if (isEnterprise) {
			isYearly = true;
		} else if (numUsers < ANNUAL_MIN_USERS) {
			isYearly = false;
		}
	});

	let price: string = $derived.by(() => {
		let total: number = 0;

		// Banded user pricing — clamp to MIN_USERS floor so dragging into the
		// grey zone doesn't show < 5-user prices.
		const effectiveUsers = Math.max(numUsers, MIN_USERS);
		const band1 = Math.min(effectiveUsers, 25);
		const band2 = Math.max(0, Math.min(effectiveUsers - 25, 25));
		if (isYearly) {
			total = band1 * BAND1_ANNUAL + band2 * BAND2_ANNUAL;
		} else {
			total = band1 * BAND1_MONTHLY + band2 * BAND2_MONTHLY;
		}

		// Add enabled add-ons to total
		addOns.forEach(addOn => {
			if (addOn.enabled) {
				total += addOn.price;
			}
		});
		return new Intl.NumberFormat("en-US", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(total);
	});

	let annualSaving: number = $derived.by(() => {
		const effectiveUsers = Math.max(numUsers, MIN_USERS);
		const band1 = Math.min(effectiveUsers, 25);
		const band2 = Math.max(0, Math.min(effectiveUsers - 25, 25));
		const monthlyTotal = band1 * BAND1_MONTHLY + band2 * BAND2_MONTHLY;
		const annualTotal = band1 * BAND1_ANNUAL + band2 * BAND2_ANNUAL;
		return (monthlyTotal - annualTotal) * 12;
	});

	let toggleIsYearly = () => {
		isYearly = !isYearly;
	};

	let pulseMinUsers = () => {
		if (!minUsersEl) return;
		minUsersEl.animate(
			[
				{ color: '#888', backgroundColor: 'transparent' },
				{ color: '#ff3e00', backgroundColor: 'rgba(255, 62, 0, 0.2)' },
				{ color: '#888', backgroundColor: 'transparent' },
				{ color: '#ff3e00', backgroundColor: 'rgba(255, 62, 0, 0.2)' },
				{ color: '#888', backgroundColor: 'transparent' },
			],
			{ duration: 2000, easing: 'ease-in-out' }
		);
	};

	let slideToMin = () => {
		if (numUsers >= MIN_USERS) return;
		pulseMinUsers();
		const startValue = numUsers;
		const distance = MIN_USERS - startValue;
		const startTime = performance.now();
		const duration = 250;
		const step = (now: number) => {
			const elapsed = now - startTime;
			const t = Math.min(elapsed / duration, 1);
			numUsers = Math.round(startValue + distance * t);
			if (t < 1) requestAnimationFrame(step);
			else numUsers = MIN_USERS;
		};
		requestAnimationFrame(step);
	};

	let togglePopover = (addOnId: string) => {
		activePopover = activePopover === addOnId ? null : addOnId;
	};

	let handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element;
		if (activePopover && !target.closest('.add-on-item-compact')) {
			activePopover = null;
		}
	};
	onMount(() => {
		ready = true;
		document.addEventListener('click', handleClickOutside);
	});

	onDestroy(() => {
		yyyy.logo.time = 877.5;
		yyyy.logo.usage = "wait";
		document.removeEventListener('click', handleClickOutside);
	});
</script>

{#if ready}
	<!-- <svelte:window on:pushstate={routing} /> -->
	<div class="column is-two-thirds not-too-wide">
		<div class="card lvwr-card" class:zeroBG={!ready}>
			<div
				class="card-content lvwr-card-content"
				transition:fly|local={{ y: -200 }}
			>
				<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>Pricing</h1>
				<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
					<!-- 
				<div class="field">
					<input
						id="switchColorDefault"
						type="checkbox"
						name="switchColorDefault"
						class="switch"
						checked="checked"
					/>
					<label for="switchColorDefault">Switch default</label>
				</div>

				<div class="field has-addons has-addons-centered">
					<p class="control">
						<button class="button is-info"> - </button>
					</p>
					<p class="control">
						<input class="input" type="text" bind:value={numUsers} placeholder="Amount of money" />
					</p>
					<p class="control">
						<button class="button is-primary"> + </button>
					</p>
				</div>-->
					<div class="slider-section">
						<div class="slider-header">
							<span class="pricing-label-compact">Users</span>
						</div>
						<div class="slider-track-wrapper">
							<div class="knob-label" style="left: calc({((numUsers - 1) / 99) * 100}% + {12 - ((numUsers - 1) / 99) * 24}px)">
								{numUsers}
							</div>
							<div class="band-segments">
								<div class="band-segment band-disabled"></div>
								<div class="band-segment band-1"></div>
								<div class="band-segment band-2"></div>
								<div class="band-segment band-enterprise"></div>
							</div>
							<input
								type="range"
								class="lvwr-slider"
								min="1"
								max="100"
								bind:value={numUsers}
								onchange={slideToMin}
							/>
						</div>
						<div class="band-info">
							<div class="band-info-item" class:band-active={numUsers >= MIN_USERS && numUsers <= 25}>
								<span class="band-dot band-dot-1"></span>
								<span>5-25: <strong>${BAND1_MONTHLY}</strong>/user</span>
							</div>
							<div class="band-info-item" class:band-active={numUsers > 25 && numUsers <= 50}>
								<span class="band-dot band-dot-2"></span>
								<span>26-50: <strong>${BAND2_MONTHLY}</strong>/user</span>
							</div>
							<div class="band-info-item" class:band-active={numUsers > 50}>
								<span class="band-dot band-dot-enterprise"></span>
								<span>51+: <strong>Enterprise</strong></span>
							</div>
						</div>
					</div>

					<div class="storage-note">
						<span bind:this={minUsersEl} class="min-users-pill">Minimum {MIN_USERS} users.</span>
						<span class="storage-note-sep"> • </span>
						<span>100 GB storage included with every plan. Additional storage at $5/GB/month.</span>
					</div>

					<div class="add-ons-section">
						<div class="add-ons-grid">
							{#each addOns as addOn}
								<div class="add-on-item-compact">
									<input
										id="addon-{addOn.id}"
										type="checkbox"
										name="addon-{addOn.id}"
										class="switch is-small"
										bind:checked={addOn.enabled}
									/>
									<label for="addon-{addOn.id}" class="add-on-label-compact">
										<span class="add-on-name-row">
											<strong>{addOn.name}</strong>
											<button
												class="info-button"
												onclick={() => togglePopover(addOn.id)}
												type="button"
											>
												<Fa icon={faInfoCircle} size="sm" />
											</button>
										</span>
										<span class="add-on-price">${addOn.price}/mo</span>
									</label>
									{#if activePopover === addOn.id}
										<div class="popover" transition:fly={{ y: -10, duration: 200 }}>
											<div class="popover-content">
												<button 
													class="popover-close"
													onclick={() => togglePopover(addOn.id)}
													type="button"
												>
													<Fa icon={faTimes} size="sm" />
												</button>
												<strong>{addOn.title}</strong>
												<p>{addOn.description}</p>
											</div>
										</div>
									{/if}
								</div>
							{/each}
						</div>
					</div>

					<div class="price-summary">
						<div class="billing-item">
							<input
								id="is-yearly"
								type="checkbox"
								name="is-yearly"
								class="switch is-small"
								checked={isYearly}
								onclick={toggleIsYearly}
								disabled={numUsers < ANNUAL_MIN_USERS}
							/>
							<label for="is-yearly" class="add-on-label-compact billing-label" class:disabled-label={numUsers < ANNUAL_MIN_USERS}>
								<strong>Annual Commitment</strong>
								{#if numUsers < ANNUAL_MIN_USERS}
									<span class="add-on-note">10+ users</span>
								{:else if isYearly && !isEnterprise}
									<span class="add-on-saving">save ${annualSaving.toLocaleString()}/yr</span>
								{/if}
							</label>
						</div>
						{#if isEnterprise}
							<span class="price-summary-text">Contact us for Enterprise pricing</span>
						{:else}
							<span class="price-summary-text">${price}/month</span>
						{/if}
					</div>

					<div class="onboarding-section" class:onboarding-open={showOnboarding}>
						<button class="onboarding-header" onclick={() => showOnboarding = !showOnboarding}>
							<div class="onboarding-header-left">
								<span class="onboarding-icon">🚀</span>
								<div>
									<strong>Add Onboarding</strong>
									<p class="onboarding-subtitle">Data migration, network modeling, data remediation & training — from $8,775</p>
								</div>
							</div>
							<span class="onboarding-arrow" class:onboarding-arrow-open={showOnboarding}>▲</span>
						</button>
						{#if showOnboarding}
							<div class="onboarding-cards" transition:fly={{ y: -10, duration: 200 }}>
								<label class="onboarding-card" class:onboarding-card-selected={onboardingPlan === 'jumpstart'}>
									<input type="radio" name="onboarding-plan" value="jumpstart" bind:group={onboardingPlan} />
									<h4>Jumpstart</h4>
									<p class="onboarding-price">$8,775</p>
									<p class="onboarding-timeline">3 - 4 weeks</p>
									<ul class="onboarding-features">
										<li>Up to 100 feeders</li>
										<li>GIS data import</li>
										<li>Data audit, cleanup & remediation</li>
										<li>Connectivity validation</li>
										<li>3 virtual training sessions</li>
										<li>3 month go-live support</li>
									</ul>
									<p class="onboarding-ideal">Small utilities with clean existing GIS data</p>
								</label>
								<label class="onboarding-card" class:onboarding-card-selected={onboardingPlan === 'enterprise'}>
									<input type="radio" name="onboarding-plan" value="enterprise" bind:group={onboardingPlan} />
									<h4>Enterprise</h4>
									<p class="onboarding-price">Contact Us</p>
									<p class="onboarding-timeline">Everything from jumpstart plus</p>
									<ul class="onboarding-features">
										<li>Unlimited feeders</li>
										<li>Custom system integrations</li>
										<li>Custom dashboards & reporting</li>
										<li>On-site or virtual training</li>
										<li>Dedicated implementation manager</li>
										<li>Continuous premier support</li>
									</ul>
									<p class="onboarding-ideal">Tailored onboarding scoped to your utility's needs</p>
								</label>
							</div>
							<div class="onboarding-summary">
								{#if onboardingPlan === 'jumpstart'}
									<span><strong>Jumpstart Onboarding</strong> — one-time setup fee.</span>
									<span class="onboarding-summary-price">$8,775</span>
								{:else}
									<span><strong>Enterprise Onboarding</strong> — one-time setup fee, quoted during discovery call</span>
									<span class="onboarding-summary-price">Contact Us</span>
								{/if}
							</div>
						{/if}
					</div>
				</div>
			</div>
			<footer
				class="card-footer"
				class:dim={!ready}
				class:bri={ready}
				transition:fly|local={{ y: 200 }}
			>
				<div class="card-footer-item sign-in-up-footer">
					<button
						onclick={() => {
							goto("/");
						}}
						class="button lvwr-toggle">Back</button
					>
					<button onclick={() => goto("/schedule-a-demo")} class="button lvwr-toggle">Schedule a Call</button>
				</div>
			</footer>
		</div>
	</div>
{/if}

<style>
	.lvwr-card-content {
		max-height: 70vh;
		overflow-y: auto;
	}

	.button.is-ghost {
		background-color: transparent;
		border: none;
	}

	.field {
		margin-top: 1rem;
	}

	.slider-section {
		margin-bottom: 0.75rem;
	}

	.slider-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.5rem;
	}

	.pricing-label-compact {
		font-weight: 600;
		font-size: 0.9rem;
		color: #333;
	}

	.storage-note-sep {
		margin: 0 0.35rem;
		color: #ccc;
	}

	.min-users-pill {
		color: #888;
		padding: 0 0.25rem;
		border-radius: 3px;
		display: inline-block;
	}

	.slider-track-wrapper {
		position: relative;
		height: 60px;
		padding-top: 28px;
	}

	.knob-label {
		position: absolute;
		top: -13px;
		transform: translateX(-50%);
		font-size: 1.1rem;
		font-weight: 700;
		color: #ff3e00;
		pointer-events: none;
		z-index: 3;
	}

	.band-segments {
		position: absolute;
		top: 50%;
		left: 0;
		right: 0;
		height: 10px;
		transform: translateY(-50%);
		display: flex;
		border-radius: 5px;
		overflow: hidden;
		pointer-events: none;
	}

	.band-segment {
		height: 100%;
	}

	.band-disabled {
		flex: 4;
		background: #ccc;
		opacity: 0.5;
	}

	.band-1 {
		flex: 20;
		background: #ff3e00;
		opacity: 0.18;
	}

	.band-2 {
		flex: 25;
		background: #ff3e00;
		opacity: 0.35;
	}

	.band-enterprise {
		flex: 50;
		background: #ff3e00;
		opacity: 0.85;
	}

	:global(.lvwr-slider) {
		position: absolute !important;
		top: 0 !important;
		left: 0 !important;
		width: 100% !important;
		height: 100% !important;
		z-index: 2 !important;
		-webkit-appearance: none !important;
		appearance: none !important;
		background: transparent !important;
		cursor: pointer !important;
		margin: 0 !important;
		padding: 0 !important;
	}

	:global(.lvwr-slider::-webkit-slider-runnable-track) {
		height: 10px !important;
		background: transparent !important;
		border-radius: 5px !important;
		border: none !important;
	}

	:global(.lvwr-slider::-webkit-slider-thumb) {
		-webkit-appearance: none !important;
		appearance: none !important;
		width: 24px !important;
		height: 24px !important;
		border-radius: 50% !important;
		background: #ff3e00 !important;
		border: 3px solid #fff !important;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
		margin-top: -7px !important;
		cursor: pointer !important;
	}

	:global(.lvwr-slider::-moz-range-track) {
		height: 10px !important;
		background: transparent !important;
		border-radius: 5px !important;
		border: none !important;
	}

	:global(.lvwr-slider::-moz-range-thumb) {
		width: 24px !important;
		height: 24px !important;
		border-radius: 50% !important;
		background: #ff3e00 !important;
		border: 3px solid #fff !important;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
		cursor: pointer !important;
	}

	.band-info {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: 0.25rem;
		min-height: 22px;
	}

	.band-info-item {
		font-size: 0.75rem;
		color: #666;
		display: flex;
		align-items: center;
		gap: 0.3rem;
	}

	.band-dot {
		display: inline-block;
		width: 8px;
		height: 8px;
		border-radius: 50%;
	}

	.band-dot-1 {
		background: rgba(255, 62, 0, 0.18);
	}

	.band-dot-2 {
		background: rgba(255, 62, 0, 0.35);
	}

	.band-dot-enterprise {
		background: rgba(255, 62, 0, 0.85);
	}

	.band-active {
		color: #ff3e00;
		font-weight: 600;
		text-decoration: underline;
		font-size: 0.85rem;
	}

	.storage-note {
		font-size: 0.8rem;
		color: #888;
		text-align: center;
		padding: 0.4rem 0;
		margin-bottom: 0.25rem;
	}

	.add-ons-section {
		margin: 0.25rem 0;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		background-color: rgba(255, 255, 255, 0.1);
		border-radius: 4px;
		border: 1px solid #e0e0e0;
	}

	:global(.add-ons-section .switch[type="checkbox"]:checked + label::before),
	:global(.price-summary .switch[type="checkbox"]:checked + label::before) {
		background: #ff3e00 !important;
		border-color: #ff3e00 !important;
	}

	.add-ons-section h3 {
		margin-bottom: 0.5rem;
		color: #ff3e00;
		font-weight: 600;
	}

	.add-ons-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		justify-content: space-between;
	}

	.add-on-item-compact {
		flex: 1;
		min-width: 180px;
		padding: 0.35rem;
		background-color: rgba(255, 255, 255, 0.05);
		border-radius: 4px;
		position: relative;
	}

	.add-on-label-compact {
		display: flex !important;
		flex-direction: row !important;
		align-items: center !important;
		justify-content: space-between;
		margin-left: 2rem;
		line-height: 1.2;
		position: relative;
		font-size: 0.85rem;
	}

	.add-on-name-row {
		display: flex;
		align-items: center;
	}

	.add-on-price {
		font-size: 0.8rem;
		color: #888;
		font-weight: 400;
		margin-left: 0.75rem;
		white-space: nowrap;
	}

	.billing-label {
		flex-direction: row !important;
		align-items: center !important;
		justify-content: flex-start;
		width: auto;
	}

	.add-on-saving {
		color: #1a7a3a;
		font-weight: 600;
		font-size: 0.8rem;
		margin-left: 1em;
	}

	.billing-item {
		flex: 0 1 auto;
		padding: 0.35rem;
		position: relative;
		background-color: transparent;
		border: none;
	}

	.disabled-label {
		opacity: 0.5;
	}

	.add-on-note {
		font-size: 0.8rem;
		color: #999;
		margin-left: 1em;
	}

	.info-button {
		background: none;
		border: none;
		color: #3273dc;
		cursor: pointer;
		margin-left: 0.5rem;
		padding: 0.25rem;
		border-radius: 50%;
		transition: all 0.2s;
		vertical-align: baseline;
	}

	.info-button:hover {
		background-color: rgba(50, 115, 220, 0.1);
		color: #ff3e00;
	}

	.popover {
		position: fixed;
		/* top: 50%; */
		/* left: 50%; */
		/* transform: translate(-50%, -5%); */
		width: 300px;
		z-index: 1000;
		max-height: 200px;
		overflow-y: auto;
	}

	/* Mobile positioning fix */
	@media (max-width: 768px) {
		.popover {
			position: fixed;
			bottom: 20%;
			left: 50%;
			transform: translateX(-50%) translateY(-80%);
			width: calc(100% - 40px);
			max-width: 350px;
		}
	}

	.popover-content {
		background: white;
		border: 1px solid #ddd;
		border-radius: 6px;
		padding: 0.75rem;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		font-size: 0.9rem;
		line-height: 1.4;
	}

	.popover-content strong {
		color: #ff3e00;
		display: block;
		margin-bottom: 0.5rem;
	}

	.popover-content p {
		margin: 0;
		color: #555;
	}

	.popover-close {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		background: none;
		border: none;
		color: #999;
		cursor: pointer;
		padding: 0.25rem;
		border-radius: 50%;
		transition: all 0.2s;
		line-height: 1;
	}

	.popover-close:hover {
		background-color: rgba(0, 0, 0, 0.1);
		color: #ff3e00;
	}

	.price-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.5rem 0;
		margin-bottom: 0.25rem;
	}

	.price-summary-text {
		font-size: 1.5rem;
		font-weight: 700;
		color: #ff3e00;
	}

	.onboarding-section {
		margin: 0.25rem 0;
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		overflow: hidden;
	}

	.onboarding-open {
		border-color: #ff3e00;
	}

	.onboarding-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 0.75rem 1rem;
		background: none;
		border: none;
		cursor: pointer;
		text-align: left;
	}

	.onboarding-header:hover {
		background-color: rgba(0, 0, 0, 0.02);
	}

	.onboarding-header-left {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.onboarding-icon {
		font-size: 1.5rem;
	}

	.onboarding-subtitle {
		margin: 0;
		font-size: 0.8rem;
		color: #888;
	}

	.onboarding-arrow {
		font-size: 0.7rem;
		color: #999;
		transition: transform 0.2s;
		transform: rotate(180deg);
	}

	.onboarding-arrow-open {
		transform: rotate(0deg);
	}

	.onboarding-cards {
		display: flex;
		gap: 1rem;
		padding: 0 1rem;
	}

	.onboarding-card {
		flex: 1;
		padding: 1rem;
		border: 2px solid #e0e0e0;
		border-radius: 4px;
		cursor: pointer;
		transition: border-color 0.2s;
	}

	.onboarding-card:hover {
		border-color: #ccc;
	}

	.onboarding-card-selected {
		border-color: #ff3e00;
	}

	.onboarding-card input[type="radio"] {
		accent-color: #ff3e00;
		margin-bottom: 0.5rem;
	}

	.onboarding-card h4 {
		display: inline;
		margin: 0 0 0 0.25rem;
		font-weight: 600;
		color: #333;
	}

	.onboarding-price {
		font-size: 1.25rem;
		font-weight: 700;
		color: #ff3e00;
		margin: 0.5rem 0 0 0;
	}

	.onboarding-timeline {
		font-size: 0.85rem;
		color: #666;
		margin: 0.25rem 0 0.5rem 0;
	}

	.onboarding-features {
		list-style: none;
		padding: 0;
		margin: 0 0 0.5rem 0;
		font-size: 0.85rem;
	}

	.onboarding-features li::before {
		content: "✓ ";
		color: #ff3e00;
		font-weight: 700;
	}

	.onboarding-features li {
		margin-bottom: 0.2rem;
	}

	.onboarding-ideal {
		font-size: 0.8rem;
		color: #888;
		font-style: italic;
		margin: 0.5rem 0 0 0;
	}

	.onboarding-summary {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.75rem 1rem;
		margin: 1rem;
		border-top: 1px solid #e0e0e0;
		font-size: 0.85rem;
	}

	.onboarding-summary-price {
		font-weight: 700;
		color: #ff3e00;
		font-size: 1.1rem;
	}
</style>
